import { useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { AgentCustomPropertiesActions } from 'store/features/agent-custom-properties/actions';
import type {
  AgentCustomPropertyDataType,
  AgentCustomPropertyName,
  ISetAgentCustomPropertyCallbacks,
} from 'store/features/agent-custom-properties/interfaces';
import {
  hasFetchedAgentCustomProperties,
  getAgentCustomProperty,
} from 'store/features/agent-custom-properties/selectors';

export function useAgentCustomProperty<T extends AgentCustomPropertyDataType = string>(
  propertyName: AgentCustomPropertyName,
  initialValue: T | null = null,
): [T, (value: T, callbacks?: ISetAgentCustomPropertyCallbacks) => void] {
  const dispatch = useDispatch();
  const agentProperty = useSelector((state) => getAgentCustomProperty(state, propertyName));
  const isPropsFetched = useSelector(hasFetchedAgentCustomProperties);
  const property = agentProperty ?? initialValue;

  const handleSetProperty = useCallback(
    (value: T, callbacks?: ISetAgentCustomPropertyCallbacks): void => {
      if (!isPropsFetched) {
        callbacks?.onFailure?.();

        return;
      }

      if (property === value) {
        callbacks?.onSuccess?.();

        return;
      }

      dispatch(
        AgentCustomPropertiesActions.setAgentCustomProperty(
          {
            [propertyName]: value,
          },
          callbacks,
        ),
      );
    },
    [dispatch, property, isPropsFetched, propertyName],
  );

  return [property as T, handleSetProperty];
}
